/**
 * Micronutrients — client mirror of functions/src/utils/micros.ts.
 * Flat optional fields (sodiumMg, potassiumMg, ...) matching the field names
 * NutrientTable's EXTENDED_ROWS renders. Units are baked into the name
 * (…G = grams, …Mg = milligrams, …Mcg = micrograms).
 */

export const MICRO_KEYS = [
  'saturatedFatG',
  'polyunsaturatedFatG',
  'monounsaturatedFatG',
  'transFatG',
  'addedSugarsG',
  'cholesterolMg',
  'sodiumMg',
  'potassiumMg',
  'magnesiumMg',
  'calciumMg',
  'ironMg',
  'zincMg',
  'vitaminAMcg',
  'vitaminCMg',
  'vitaminDMcg',
  'vitaminEMg',
  'vitaminKMcg',
  'thiaminMg',
  'riboflavinMg',
  'niacinMg',
  'vitaminB6Mg',
  'folateMcg',
  'vitaminB12Mcg',
] as const;

export type MicroKey = (typeof MICRO_KEYS)[number];
export type Micros = Partial<Record<MicroKey, number>>;

const MICRO_MAX = 100000;

/**
 * Tolerant aliases for model key slips (wrong unit suffix on mcg fields).
 * Values are assumed to already be in the canonical unit — models echo
 * typical mcg-scale numbers regardless of the suffix they emit.
 */
const MICRO_KEY_ALIASES: Record<string, MicroKey> = {
  vitaminAMg: 'vitaminAMcg',
  vitaminDMg: 'vitaminDMcg',
  vitaminKMg: 'vitaminKMcg',
  folateMg: 'folateMcg',
  vitaminB12Mg: 'vitaminB12Mcg',
  vitaminEMcg: 'vitaminEMg',
};


/** Copy finite, non-negative micro fields from an arbitrary object (1-decimal rounded). */
export function pickMicros(source: unknown): Micros {
  const out: Micros = {};
  if (!source || typeof source !== 'object') return out;
  const record = source as Record<string, unknown>;
  const readInto = (key: MicroKey, raw: unknown) => {
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN;
    if (Number.isFinite(n) && n >= 0 && n <= MICRO_MAX) {
      out[key] = Math.round(n * 10) / 10;
    }
  };
  for (const [alias, key] of Object.entries(MICRO_KEY_ALIASES)) {
    if (alias in record) readInto(key, record[alias]);
  }
  for (const key of MICRO_KEYS) {
    if (key in record) readInto(key, record[key]);
  }
  return out;
}

/** Sum micros across records; keys absent everywhere stay absent. */
export function sumMicros(items: unknown[]): Micros {
  const totals: Micros = {};
  for (const item of items) {
    const micros = pickMicros(item);
    for (const [key, value] of Object.entries(micros) as [MicroKey, number][]) {
      totals[key] = Math.round(((totals[key] ?? 0) + value) * 10) / 10;
    }
  }
  return totals;
}

/** Scale all micro fields on a record by a multiplier (e.g. servings). */
export function scaleMicros(source: unknown, multiplier: number): Micros {
  const out: Micros = {};
  if (!Number.isFinite(multiplier) || multiplier < 0) return out;
  const micros = pickMicros(source);
  for (const [key, value] of Object.entries(micros) as [MicroKey, number][]) {
    out[key] = Math.round(value * multiplier * 10) / 10;
  }
  return out;
}
